import { ADMIN_ROLE } from "src/services/auth";

/**
 * AllocationConfig toggle. Keep the string in lockstep with teamform-api.
 * If the API PR already shipped a different name, rename this constant and
 * the GraphQL/form field together — do not add a second flag.
 */
export const ENABLE_FORECAST_READ_ONLY_OUTSIDE_GROUP_ACCESS =
  "enableForecastReadOnlyOutsideGroupAccess" as const;

/**
 * Optional GroupPermissions signal. Prefer this when the API includes it on
 * the group. Until the field is queried, FE derives editability from the
 * AllocationConfig toggle + hierarchy + role.
 */
export const CAN_EDIT_PLANNING_PERMISSION = "canEditPlanning" as const;

export type PlanningAccessAllocationConfig = {
  enableGroupAccessRestrictions?: boolean | null;
  enableForecastReadOnlyOutsideGroupAccess?: boolean | null;
};

export type PlanningAccessPermissions = {
  areGroupsInAccessGroupIdsHierarchy?: boolean | null;
  canEditPlanning?: boolean | null;
};

export type PlanningAccessGroup = {
  permissions?: PlanningAccessPermissions | null;
};

export type PlanningAccessInput = {
  allocationConfig?: PlanningAccessAllocationConfig | null | undefined;
  group?: PlanningAccessGroup | null | undefined;
  userRole?: string | null | undefined;
};

export const pickPlanningAccessConfig = (
  allocation?: PlanningAccessAllocationConfig | null
): PlanningAccessAllocationConfig => ({
  enableGroupAccessRestrictions: !!allocation?.enableGroupAccessRestrictions,
  enableForecastReadOnlyOutsideGroupAccess:
    !!allocation?.[ENABLE_FORECAST_READ_ONLY_OUTSIDE_GROUP_ACCESS],
});

export const isOutsideCustomGroupAccess = (
  group?: PlanningAccessGroup | null
): boolean => !group?.permissions?.areGroupsInAccessGroupIdsHierarchy;

export const isCustomAccessPlanningReadOnlyEnabled = (
  allocationConfig?: PlanningAccessAllocationConfig | null
): boolean =>
  !!allocationConfig?.enableGroupAccessRestrictions &&
  !!allocationConfig?.[ENABLE_FORECAST_READ_ONLY_OUTSIDE_GROUP_ACCESS];

/**
 * Custom-access toggle ON + POWER (or unknown) outside hierarchy.
 * Hierarchy-based denials are unchanged: custom access must also be on.
 */
export const shouldShowPlanningSurfacesWhenOutsideGroupAccess = (
  input: PlanningAccessInput
): boolean =>
  isCustomAccessPlanningReadOnlyEnabled(input.allocationConfig) &&
  isOutsideCustomGroupAccess(input.group);

export const canEditPlanning = (input: PlanningAccessInput): boolean => {
  if (input.userRole === ADMIN_ROLE) {
    return true;
  }

  const apiSignal = input.group?.permissions?.[CAN_EDIT_PLANNING_PERMISSION];
  if (typeof apiSignal === "boolean") {
    return apiSignal;
  }

  if (!isCustomAccessPlanningReadOnlyEnabled(input.allocationConfig)) {
    return true;
  }

  return !isOutsideCustomGroupAccess(input.group);
};

export const isPlanningAccessReadonly = (input: PlanningAccessInput): boolean =>
  !canEditPlanning(input);

export const isPlanningAccessReadonlyForRootGroup = (
  rootGroup?:
    | (PlanningAccessGroup & {
        allocationConfig?: PlanningAccessAllocationConfig | null;
        userRole?: string | null;
      })
    | null
): boolean =>
  isPlanningAccessReadonly({
    allocationConfig: rootGroup?.allocationConfig,
    group: rootGroup,
    userRole: rootGroup?.userRole,
  });

type PlanningReadonlyProjectFlags = {
  isRequestorForecastReadonly?: boolean;
  isApproverForecastReadonly?: boolean;
  isRequestorPlannerReadonly?: boolean;
  isApproverPlannerReadonly?: boolean;
};

export const applyPlanningAccessReadonlyFlags = <
  T extends PlanningReadonlyProjectFlags,
>(
  project: T
): T => ({
  ...project,
  isRequestorForecastReadonly: true,
  isApproverForecastReadonly: true,
  isRequestorPlannerReadonly: true,
  isApproverPlannerReadonly: true,
});
